// Need to use the React-specific entry point to allow generating React hooks

import { baseApi } from "../api/baseApi";

import { EventData, EventLog, Work, WorkItemList } from "../types/work.types";

// Re-export types that are used by other modules
export type { 
  EventData, 
  EventLog, 
  Work, 
  WorkItemList
} from "../types/work.types";

export { 
  WorkItemStateEnum,
  WorkItemTypeEnum,
  WorkStateEnum,
  WorkTypeEnum,
  EventDataTypeEnum,
  EventLogTypeEnum,
  WorkItemFavoriteTypeEnum
} from "../types/work.types";

// Define a service using a base URL and expected endpoints
export const timelineApiRTK = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerTimeline: builder.query<TimeLine, number>({
      query: (customerNo: number) => `/v2/crm/customer/${customerNo}/timeline`,
      providesTags: (result, error, customerNo) => [
        { type: "Timeline", id: customerNo },
      ],
      keepUnusedDataFor: 30,
    }),
    getWorkById: builder.query<Work, number>({
      query: (workId) => `/v2/crm/works/${workId}`,
      providesTags: (result, error, workId) => [{ type: "Work", id: workId }],
    }),
    getCustomerWorks: builder.query<Work[], number>({
      query: (customerNo) => `/v2/crm/customer/${customerNo}/works`,
      providesTags: (result, error, customerNo) => [
        { type: "Work", id: `customer-${customerNo}` },
      ],
    }),
    createWorkComment: builder.mutation<void, CreateWorkCommentRequest>({
      query: (data) => ({
        url: `/v2/crm/data/text`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { WorkId }) => [
        { type: "Work", id: WorkId },
        { type: "Timeline", id: "LIST" },
      ],
    }),
    updateWorkComment: builder.mutation<void, UpdateWorkCommentRequest>({
      query: ({ eventDataId, text }) => ({
        url: `/v2/crm/eventData/${eventDataId}/UpdateText`,
        method: "PUT",
        body: { text },
      }),
      invalidatesTags: (result, error, { workId }) => [
        ...(workId != null ? [{ type: "Work" as const, id: workId }] : []),
        { type: "Timeline" as const, id: "LIST" },
      ],
    }),
    // Samma endpoint som Kraftvyn använder för generella ärendefiler.
    // Ingen Content-Type sätts — webbläsaren måste själv sätta multipart-boundaryn.
    uploadWorkDocument: builder.mutation<void, { workId: number; file: File }>({
      query: ({ workId, file }) => {
        const body = new FormData();
        body.append("Document", file);
        body.append("WorkId", workId.toString());
        return {
          url: "/v2/crm/uploaddocument",
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, { workId }) => [
        { type: "Work", id: workId },
      ],
    }),
    downloadEventDataFile: builder.mutation<null, { eventDataId: number; filename: string }>({
      queryFn: async ({ eventDataId, filename }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/v2/crm/filedata/${eventDataId}/download?filename=${encodeURIComponent(filename)}`,
          responseHandler: async (response: Response) => {
            if (!response.ok) {
              const text = await response.text();
              return text;
            }
            return response.blob();
          },
        });
        if (result.error) return { error: result.error };

        const blob = result.data as Blob;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);

        return { data: null };
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCustomerTimelineQuery,
  useGetWorkByIdQuery,
  useGetCustomerWorksQuery,
  useCreateWorkCommentMutation,
  useUpdateWorkCommentMutation,
  useUploadWorkDocumentMutation,
  useDownloadEventDataFileMutation,
} = timelineApiRTK;

export interface TimeLine {
  Datas: EventData[];
  Works: Work[];
  EventLogs: EventLog[];
  WorkItems: WorkItemList[];
}

export interface CreateWorkCommentRequest {
  CustomerId: number | null;
  Description: string;
  Name: string | null;
  WorkId: number;
}

export interface UpdateWorkCommentRequest {
  eventDataId: number;
  text: string;
  workId?: number;
}
